<script setup lang="ts">
import { computed } from 'vue';
import Icon from './Icon.vue';

/**
 * KPI/stat tile for dashboards. When `value` is omitted (module not enabled / data not
 * available yet), it renders a muted placeholder dash instead of a fabricated number —
 * callers must never pass a fake value (AGENTS.md: "do not fake numbers").
 *
 * `symbol` (a Material Symbols ligature name, matching the Stitch design source) takes
 * precedence over the legacy `icon` (a PrimeIcons class string, e.g. `"pi" + " " + "pi-box"`)
 * when both are given — `icon` is kept only for callers that have not migrated yet.
 */
const props = defineProps<{
  label: string;
  value?: string | number | null;
  icon?: string;
  symbol?: string;
  hint?: string;
  tone?: 'primary' | 'secondary' | 'tertiary' | 'danger' | 'neutral';
}>();

const displayValue = computed(() => {
  if (props.value === null || props.value === undefined) return '—';
  if (typeof props.value === 'number') return Number.isFinite(props.value) ? props.value : '—';
  const trimmed = props.value.trim();
  return trimmed.length > 0 ? trimmed : '—';
});

const toneClasses = computed(() => {
  switch (props.tone) {
    case 'primary':
      return 'bg-jv-surface-low text-jv-primary';
    case 'secondary':
      return 'bg-jv-secondary-container text-jv-on-secondary-container';
    case 'tertiary':
      return 'bg-jv-tertiary-container text-jv-on-tertiary-container';
    case 'danger':
      return 'bg-jv-danger-container text-jv-on-danger-container';
    default:
      return 'bg-jv-surface-container-high text-jv-on-surface-variant';
  }
});
</script>

<template>
  <div class="jv-stat-card relative h-full min-h-32 overflow-hidden rounded-jv-xl bg-jv-surface-lowest p-4 md:p-6">
    <div class="flex items-start justify-between gap-3">
      <div class="min-w-0">
        <p class="m-0 text-xs leading-4 font-medium text-jv-on-surface-variant">{{ label }}</p>
        <p class="m-0 mt-1 text-[28px] leading-9 font-semibold tabular-nums text-jv-on-surface" dir="ltr">
          {{ displayValue }}
        </p>
      </div>
      <span v-if="icon || symbol" class="grid size-10 shrink-0 place-items-center rounded-jv-lg" :class="toneClasses">
        <Icon v-if="symbol" :name="symbol" :size="20" />
        <i v-else :class="icon" class="text-lg" />
      </span>
    </div>
    <p v-if="hint" class="m-0 mt-3 rounded-jv-md bg-jv-surface-low p-2 text-xs text-jv-on-surface-variant">
      {{ hint }}
    </p>
    <slot />
    <span
      aria-hidden="true"
      class="pointer-events-none absolute -bottom-6 -end-6 size-24 rounded-full bg-jv-primary-container opacity-[0.04]"
    />
  </div>
</template>
